import { motion } from "framer-motion";
import { ArrowRight, Briefcase, Factory, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import AffiliateCard from "@/components/affiliates/AffiliateCard";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { AFFILIATE_CATEGORIES } from "@/lib/affiliates";

const categoryIcons = {
  freelance: Briefcase,
  sourcing_production: Factory,
};

const AffiliatesLanding = () => {
  const { data: featured } = useQuery({
    queryKey: ["affiliates", "featured"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliates_public" as any)
        .select("*")
        .eq("status", "active")
        .order("featured", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(6);
      if (error) throw error;
      return data as any[];
    },
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-36 pb-20 relative overflow-hidden bg-inherit">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="container mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">
              Affiliates
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-6">
              Extend Your Project Team With{" "}
              <span className="text-gradient-primary">Vetted Providers</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl mx-auto">
              When a project needs more than your in-house capacity, our affiliate network
              of freelancers, makers, and production partners is ready to plug in.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/affiliates/directory">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
                  Browse the Directory <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/affiliates/apply">
                <Button size="lg" variant="outline">
                  Apply to Join
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-card border-y border-border">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
              Two Ways to Plug In
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Whether you need an expert collaborator or a production partner, find a fit you can trust.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {AFFILIATE_CATEGORIES.map((cat) => {
              const Icon = categoryIcons[cat.value];
              return (
                <Link
                  key={cat.value}
                  to={`/affiliates/directory?category=${cat.value}`}
                  className="group bg-background border border-border rounded-lg p-7 hover:border-primary/40 hover:shadow-glow transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-display text-xl font-bold mb-2">{cat.label}</h3>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{cat.blurb}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.subcategories.slice(0, 5).map((s) => (
                      <span key={s} className="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                        {s}
                      </span>
                    ))}
                  </div>
                  <span className="mt-5 inline-flex items-center text-sm text-primary font-semibold group-hover:gap-2 gap-1 transition-all">
                    Explore providers <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-primary text-sm font-semibold tracking-wider uppercase flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> Featured providers
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-bold mt-2">
                Recently added to the network
              </h2>
            </div>
            <Link to="/affiliates/directory" className="text-sm text-primary font-semibold hover:underline shrink-0">
              View all →
            </Link>
          </div>

          {!featured || featured.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-xl bg-card/50">
              <p className="text-muted-foreground">
                The directory is just getting started. Check back soon — or{" "}
                <Link to="/affiliates/apply" className="text-primary font-semibold hover:underline">
                  apply to be one of the first listed
                </Link>.
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featured.map((a, i) => (
                <AffiliateCard key={a.id} affiliate={a} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-card border-t border-border">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              Are You a Provider?
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Join the affiliate network to receive referrals from clients running funded projects across Alabama.
              No platform fees, no exclusivity — just qualified leads.
            </p>
            <Link to="/affiliates/apply">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
                Apply to Join <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default AffiliatesLanding;